import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, FileText, BarChart3, PieChart, TrendingUp, Building2, Car, Fuel, Calendar, Filter, Loader2, Eye, Search, ArrowUp, ArrowDown, Minus, ArrowLeftRight, Lock, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import AccessDeniedBlock from '../components/AccessDeniedBlock';
import FuelFilter from '../components/FuelFilter';
import { Input } from '../components/ui/input';
import { CardDescription } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, LineChart, Line, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, Radar
} from 'recharts';

// Color palette for charts
const COLORS = ['#003366', '#0066CC', '#00A3E0', '#66C2FF', '#99D6FF', '#CCE5FF', '#1E3A5F', '#2E5984', '#4A7BA7', '#6699CC'];
const PIE_COLORS = ['#003366', '#CC0000', '#009900', '#FF6600', '#9900CC', '#006666', '#CC6600', '#0099CC', '#CC0066', '#669900'];

export default function RelatoriosPage() {
  const { isSecretaria } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('secretarias');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Filtros individuais
  const [postosSelecionados, setPostosSelecionados] = useState([]);
  const [secretariasSelecionadas, setSecretariasSelecionadas] = useState([]);
  const [combustiveisSelecionados, setCombustiveisSelecionados] = useState([]);
  const [allPostos, setAllPostos] = useState([]);
  const [allSecretarias, setAllSecretarias] = useState([]);
  const [allCombustiveis, setAllCombustiveis] = useState([]);
  const [combustiveisDisponiveis, setCombustiveisDisponiveis] = useState([]);
  
  const [dashboardData, setDashboardData] = useState({
    secretarias: [],
    postos: [],
    veiculos: [],
    funcionarios: [],
    combustiveis: [],
    totals: { value: 0, liters: 0, records: 0 }
  });
  const [comparativoData, setComparativoData] = useState(null);
  const [loadingComparativo, setLoadingComparativo] = useState(false);
  const [compResult, setCompResult] = useState(null);
  const [loadingComp, setLoadingComp] = useState(false);
  // Period A (current) and Period B (reference)
  const now = new Date();
  const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
  const [periodAStart, setPeriodAStart] = useState(firstDayThisMonth);
  const [periodAEnd, setPeriodAEnd] = useState(lastDayThisMonth);
  const [periodBStart, setPeriodBStart] = useState(firstDayLastMonth);
  const [periodBEnd, setPeriodBEnd] = useState(lastDayLastMonth);
  const [openPA1, setOpenPA1] = useState(false);
  const [openPA2, setOpenPA2] = useState(false);
  const [openPB1, setOpenPB1] = useState(false);
  const [openPB2, setOpenPB2] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [searchCA, setSearchCA] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  useEffect(() => {
    fetchAllFilters();
  }, []);

  // Atualizar combustiveis disponiveis quando postos mudam
  useEffect(() => {
    const fetchCombustiveisPorPosto = async () => {
      if (postosSelecionados.length === 0) {
        setCombustiveisDisponiveis(allCombustiveis);
        return;
      }
      try {
        const res = await api.get(`/combustiveis/por-posto?posto_ids=${postosSelecionados.join(',')}`);
        const idsPermitidos = new Set(res.data);
        setCombustiveisDisponiveis(allCombustiveis.filter(c => idsPermitidos.has(c.id)));
        setCombustiveisSelecionados(prev => prev.filter(id => idsPermitidos.has(id)));
      } catch {
        setCombustiveisDisponiveis(allCombustiveis);
      }
    };
    fetchCombustiveisPorPosto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postosSelecionados, allCombustiveis]);

  useEffect(() => {
    fetchDashboardData();
    fetchPreviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, postosSelecionados, secretariasSelecionadas, combustiveisSelecionados]);

  // Fetch comparative data when switching to that tab
  useEffect(() => {
    if (activeTab === 'comparativo' && !comparativoData) {
      fetchComparativoData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchComparativoData = async () => {
    setLoadingComparativo(true);
    try {
      const res = await api.get('/reports/comparativo');
      setComparativoData(res.data);
      // Auto-compare on load
      fetchCompare();
    } catch (error) {
      console.error('Erro ao carregar dados comparativos:', error);
    } finally {
      setLoadingComparativo(false);
    }
  };

  const fetchCompare = async () => {
    setLoadingComp(true);
    try {
      const params = {
        periodo_a_inicio: format(periodAStart, 'yyyy-MM-dd'),
        periodo_a_fim: format(periodAEnd, 'yyyy-MM-dd'),
        periodo_b_inicio: format(periodBStart, 'yyyy-MM-dd'),
        periodo_b_fim: format(periodBEnd, 'yyyy-MM-dd'),
      };
      const res = await api.get('/reports/comparativo-periodos', { params });
      setCompResult(res.data);
    } catch (error) {
      toast.error('Erro ao comparar períodos');
    } finally {
      setLoadingComp(false);
    }
  };

  // Buscar lista de postos, secretarias e combustiveis para filtros
  const fetchAllFilters = async () => {
    try {
      const [postosRes, secretariasRes, combustiveisRes] = await Promise.all([
        api.get('/postos'),
        api.get('/secretarias'),
        api.get('/combustiveis')
      ]);
      setAllPostos(postosRes.data);
      setAllSecretarias(secretariasRes.data);
      setAllCombustiveis(combustiveisRes.data);
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    }
  };

  // Toggle seleção de posto
  const togglePosto = (postoId) => {
    setPostosSelecionados(prev => 
      prev.includes(postoId) 
        ? prev.filter(id => id !== postoId)
        : [...prev, postoId]
    );
  };

  // Toggle seleção de secretaria
  const toggleSecretaria = (secretariaId) => {
    setSecretariasSelecionadas(prev => 
      prev.includes(secretariaId) 
        ? prev.filter(id => id !== secretariaId)
        : [...prev, secretariaId]
    );
  };

  // Selecionar/Deselecionar todos os postos
  const toggleAllPostos = () => {
    if (postosSelecionados.length === allPostos.length) {
      setPostosSelecionados([]);
    } else {
      setPostosSelecionados(allPostos.map(p => p.id));
    }
  };

  // Selecionar/Deselecionar todas as secretarias
  const toggleAllSecretarias = () => {
    if (secretariasSelecionadas.length === allSecretarias.length) {
      setSecretariasSelecionadas([]);
    } else {
      setSecretariasSelecionadas(allSecretarias.map(s => s.id));
    }
  };

  // Filtrar registros do preview pela CA digitada
  const filteredPreviewRecords = useMemo(() => {
    if (!previewData || !previewData.records) return [];
    if (!searchCA.trim()) return previewData.records;
    const term = searchCA.trim().toLowerCase();
    return previewData.records.filter(r => 
      (r.ca || '').toString().toLowerCase().includes(term)
    );
  }, [previewData, searchCA]);

  const filteredPreviewTotal = useMemo(() => {
    return filteredPreviewRecords.filter(r => r.status_contabil !== 'vermelho').reduce((s, r) => s + (r.valor || 0), 0);
  }, [filteredPreviewRecords]);

  const toggleSelectRecord = (abastecimentoId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(abastecimentoId)) {
        next.delete(abastecimentoId);
      } else {
        next.add(abastecimentoId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPreviewRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPreviewRecords.map(r => r.abastecimento_id)));
    }
  };

  const selectedTotal = useMemo(() => {
    return filteredPreviewRecords.filter(r => selectedIds.has(r.abastecimento_id)).reduce((s, r) => s + (r.valor || 0), 0);
  }, [filteredPreviewRecords, selectedIds]);

  const downloadSelectedPDF = async () => {
    if (selectedIds.size === 0) {
      toast.error('Selecione pelo menos um registro.');
      return;
    }
    setDownloadingPdf('selecionados');
    try {
      const params = { ids: Array.from(selectedIds).join(',') };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await api.get('/reports/geral-pdf', {
        params,
        responseType: 'blob',
        timeout: 120000
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_selecionados_${selectedIds.size}CAs.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF gerado com ${selectedIds.size} registro(s) selecionado(s)!`);
    } catch (error) {
      console.error('Error downloading selected PDF:', error);
      toast.error('Erro ao gerar PDF dos selecionados.');
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Bloquear acesso para perfil secretaria (depois de todos os hooks)
  if (isSecretaria()) {
    return (
      <AccessDeniedBlock 
        title="Acesso Restrito" 
        message="Usuarios com perfil Secretaria nao tem permissao para visualizar relatorios. Acesse a pagina de Secretarias para gerenciar seu departamento."
      />
    );
  }

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (postosSelecionados.length > 0) params.postos = postosSelecionados.join(',');
      if (secretariasSelecionadas.length > 0) params.secretarias = secretariasSelecionadas.join(',');
      if (combustiveisSelecionados.length > 0) params.combustiveis = combustiveisSelecionados.join(',');

      const [chartsRes, statsRes] = await Promise.all([
        api.get('/dashboard/charts', { params }),
        api.get('/dashboard/stats', { params })
      ]);

      setDashboardData({
        secretarias: chartsRes.data.by_secretaria || [],
        postos: chartsRes.data.by_posto || [],
        veiculos: chartsRes.data.by_veiculo || [],
        funcionarios: [], // Will be populated from secretarias data
        combustiveis: chartsRes.data.by_combustivel || [],
        totals: {
          value: statsRes.data.total_value || 0,
          liters: statsRes.data.total_liters || 0,
          records: statsRes.data.total_records || 0
        }
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
  };

  const downloadPDF = async (reportType) => {
    setDownloadingPdf(reportType);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (postosSelecionados.length > 0) params.postos = postosSelecionados.join(',');
      if (secretariasSelecionadas.length > 0) params.secretarias_filter = secretariasSelecionadas.join(',');
      if (combustiveisSelecionados.length > 0) params.combustiveis_filter = combustiveisSelecionados.join(',');

      const response = await api.get(`/reports/${reportType}-pdf`, { 
        params,
        responseType: 'blob',
        timeout: 120000
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const postoSuffix = postosSelecionados.length > 0 && postosSelecionados.length < allPostos.length
        ? `_${postosSelecionados.length}postos`
        : '';
      const filename = reportType === 'geral' 
        ? `relatorio_geral_abastecimento${postoSuffix}.pdf` 
        : `relatorio_${reportType}${postoSuffix}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      if (postosSelecionados.length > 0 && postosSelecionados.length < allPostos.length) {
        toast.success(`PDF gerado com ${postosSelecionados.length} posto(s) selecionado(s)!`);
      } else {
        toast.success('PDF gerado com sucesso!');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      if (error.response) {
        const text = await error.response.data?.text?.();
        console.error('Server response:', text);
        toast.error(`Erro ao gerar PDF (${error.response.status})`);
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Timeout: o relatório está demorando. Tente novamente.');
      } else {
        toast.error('Erro ao gerar PDF. Verifique sua conexão.');
      }
    } finally {
      setDownloadingPdf(null);
    }
  };

  const fetchPreviewData = async () => {
    setLoadingPreview(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (postosSelecionados.length > 0) params.postos = postosSelecionados.join(',');
      if (secretariasSelecionadas.length > 0) params.secretarias = secretariasSelecionadas.join(',');
      if (combustiveisSelecionados.length > 0) params.combustiveis = combustiveisSelecionados.join(',');
      const res = await api.get('/reports/geral-preview', { params });
      setPreviewData(res.data);
    } catch (error) {
      console.error('Erro ao buscar preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.name.includes('Valor') || entry.name.includes('R$') 
                ? formatCurrency(entry.value) 
                : formatNumber(entry.value) + ' L'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const tabs = [
    { id: 'secretarias', label: 'Por Secretaria', icon: Building2, color: 'bg-blue-600' },
    { id: 'postos', label: 'Por Posto', icon: Fuel, color: 'bg-red-600' },
    { id: 'veiculos', label: 'Por Veículo', icon: Car, color: 'bg-green-600' },
    { id: 'combustiveis', label: 'Por Combustível', icon: PieChart, color: 'bg-orange-600' },
    { id: 'comparativo', label: 'Comparativo', icon: TrendingUp, color: 'bg-purple-600' },
  ];

  const renderSecretariasReport = () => {
    const data = dashboardData.secretarias.slice(0, 10);
    const totalGeral = dashboardData.secretarias.reduce((sum, s) => sum + s.value, 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-l-blue-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total de Secretarias</p>
              <p className="text-2xl font-bold text-blue-800">{dashboardData.secretarias.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-l-4 border-l-green-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-green-800">{formatCurrency(totalGeral)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-l-purple-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Média por Secretaria</p>
              <p className="text-2xl font-bold text-purple-800">
                {formatCurrency(totalGeral / (dashboardData.secretarias.length || 1))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#003366]">
              <BarChart3 size={24} />
              Ranking de Gastos por Secretaria (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 150, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                  <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Valor (R$)" fill="#003366" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#003366]">
              <PieChart size={24} />
              Distribuição Percentual por Secretaria
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] flex">
              <div className="w-2/3">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/3 flex flex-col justify-center space-y-2">
                {data.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                    <span className="truncate">{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#003366]">
              <FileText size={24} />
              Tabela Detalhada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#003366] text-white">
                    <th className="p-3 text-left">#</th>
                    <th className="p-3 text-left">Secretaria</th>
                    <th className="p-3 text-right">Valor Total</th>
                    <th className="p-3 text-right">% do Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.secretarias.map((sec, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 border-b">{idx + 1}</td>
                      <td className="p-3 border-b font-medium">{sec.name}</td>
                      <td className="p-3 border-b text-right">{formatCurrency(sec.value)}</td>
                      <td className="p-3 border-b text-right">{((sec.value / totalGeral) * 100).toFixed(2)}%</td>
                    </tr>
                  ))}
                  <tr className="bg-[#003366] text-white font-bold">
                    <td className="p-3" colSpan={2}>TOTAL GERAL</td>
                    <td className="p-3 text-right">{formatCurrency(totalGeral)}</td>
                    <td className="p-3 text-right">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderPostosReport = () => {
    const data = dashboardData.postos.map(p => ({
      ...p,
      name: p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name
    }));
    const totalGeral = dashboardData.postos.reduce((sum, p) => sum + p.value, 0);
    const totalLitros = dashboardData.postos.reduce((sum, p) => sum + p.liters, 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-white border-l-4 border-l-red-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total de Postos</p>
              <p className="text-2xl font-bold text-red-800">{dashboardData.postos.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-l-4 border-l-green-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-green-800">{formatCurrency(totalGeral)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-l-blue-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total de Litros</p>
              <p className="text-2xl font-bold text-blue-800">{formatNumber(totalLitros)} L</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-l-purple-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Preço Médio/Litro</p>
              <p className="text-2xl font-bold text-purple-800">
                {formatCurrency(totalGeral / (totalLitros || 1))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bar Chart - Value */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#CC0000]">
              <BarChart3 size={24} />
              Gastos por Posto de Combustível
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="value" name="Valor (R$)" fill="#CC0000" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart - Liters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0066CC]">
              <Fuel size={24} />
              Volume (Litros) por Posto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(value) => `${formatNumber(value, 0)} L`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="liters" name="Litros" fill="#0066CC" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#003366]">
              <PieChart size={24} />
              Distribuição de Gastos por Posto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px] flex">
              <div className="w-2/3">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={dashboardData.postos}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                      outerRadius={150}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {dashboardData.postos.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/3 flex flex-col justify-center space-y-3">
                {dashboardData.postos.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                    <span className="text-sm truncate">{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#003366]">
              <FileText size={24} />
              Tabela Detalhada por Posto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#CC0000] text-white">
                    <th className="p-3 text-left">#</th>
                    <th className="p-3 text-left">Posto</th>
                    <th className="p-3 text-right">Litros</th>
                    <th className="p-3 text-right">Valor Total</th>
                    <th className="p-3 text-right">Preço Médio/L</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.postos.map((posto, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 border-b">{idx + 1}</td>
                      <td className="p-3 border-b font-medium">{posto.name}</td>
                      <td className="p-3 border-b text-right">{formatNumber(posto.liters)} L</td>
                      <td className="p-3 border-b text-right">{formatCurrency(posto.value)}</td>
                      <td className="p-3 border-b text-right">{formatCurrency(posto.value / (posto.liters || 1))}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#CC0000] text-white font-bold">
                    <td className="p-3" colSpan={2}>TOTAL</td>
                    <td className="p-3 text-right">{formatNumber(totalLitros)} L</td>
                    <td className="p-3 text-right">{formatCurrency(totalGeral)}</td>
                    <td className="p-3 text-right">{formatCurrency(totalGeral / (totalLitros || 1))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderVeiculosReport = () => {
    const data = dashboardData.veiculos.slice(0, 10);
    const totalGeral = dashboardData.veiculos.reduce((sum, v) => sum + v.value, 0);
    const totalLitros = dashboardData.veiculos.reduce((sum, v) => sum + v.liters, 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-white border-l-4 border-l-green-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total de Veículos</p>
              <p className="text-2xl font-bold text-green-800">{dashboardData.veiculos.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-l-blue-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-blue-800">{formatCurrency(totalGeral)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-l-purple-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total de Litros</p>
              <p className="text-2xl font-bold text-purple-800">{formatNumber(totalLitros)} L</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-white border-l-4 border-l-orange-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Média por Veículo</p>
              <p className="text-2xl font-bold text-orange-800">
                {formatCurrency(totalGeral / (dashboardData.veiculos.length || 1))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#009900]">
              <Car size={24} />
              Top 10 Veículos por Consumo (Valor)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                  <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Valor (R$)" fill="#009900" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart - Liters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0066CC]">
              <TrendingUp size={24} />
              Top 10 Veículos por Volume (Litros)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis type="number" tickFormatter={(value) => `${formatNumber(value, 0)} L`} />
                  <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="liters" name="Litros" fill="#0066CC" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#003366]">
              <FileText size={24} />
              Tabela Completa de Veículos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0">
                  <tr className="bg-[#009900] text-white">
                    <th className="p-3 text-left">#</th>
                    <th className="p-3 text-left">Placa</th>
                    <th className="p-3 text-right">Litros</th>
                    <th className="p-3 text-right">Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.veiculos.map((veiculo, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 border-b">{idx + 1}</td>
                      <td className="p-3 border-b font-medium">{veiculo.name}</td>
                      <td className="p-3 border-b text-right">{formatNumber(veiculo.liters)} L</td>
                      <td className="p-3 border-b text-right">{formatCurrency(veiculo.value)}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#009900] text-white font-bold sticky bottom-0">
                    <td className="p-3" colSpan={2}>TOTAL</td>
                    <td className="p-3 text-right">{formatNumber(totalLitros)} L</td>
                    <td className="p-3 text-right">{formatCurrency(totalGeral)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderCombustiveisReport = () => {
    const data = dashboardData.combustiveis;
    const totalGeral = data.reduce((sum, c) => sum + c.value, 0);
    const totalLitros = data.reduce((sum, c) => sum + c.liters, 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-orange-50 to-white border-l-4 border-l-orange-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Tipos de Combustível</p>
              <p className="text-2xl font-bold text-orange-800">{data.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-l-4 border-l-green-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-green-800">{formatCurrency(totalGeral)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-l-blue-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total de Litros</p>
              <p className="text-2xl font-bold text-blue-800">{formatNumber(totalLitros)} L</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-l-purple-600">
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Preço Médio/Litro</p>
              <p className="text-2xl font-bold text-purple-800">
                {formatCurrency(totalGeral / (totalLitros || 1))}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Large Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#FF6600]">
              <PieChart size={24} />
              Distribuição por Tipo de Combustível (Valor)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[450px] flex">
              <div className="w-2/3">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={170}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/3 flex flex-col justify-center space-y-4">
                {data.map((entry, index) => (
                  <div key={index} className="p-3 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} />
                      <span className="font-semibold">{entry.name}</span>
                    </div>
                    <p className="text-sm text-gray-600 ml-6">Valor: {formatCurrency(entry.value)}</p>
                    <p className="text-sm text-gray-600 ml-6">Volume: {formatNumber(entry.liters)} L</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart - Volume */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#0066CC]">
              <BarChart3 size={24} />
              Volume (Litros) por Tipo de Combustível
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => `${formatNumber(value, 0)} L`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="liters" name="Litros" fill="#FF6600" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Comparison Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#003366]">
              <FileText size={24} />
              Comparativo de Combustíveis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#FF6600] text-white">
                    <th className="p-3 text-left">Combustível</th>
                    <th className="p-3 text-right">Litros</th>
                    <th className="p-3 text-right">% Litros</th>
                    <th className="p-3 text-right">Valor Total</th>
                    <th className="p-3 text-right">% Valor</th>
                    <th className="p-3 text-right">Preço Médio/L</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((comb, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 border-b font-medium">{comb.name}</td>
                      <td className="p-3 border-b text-right">{formatNumber(comb.liters)} L</td>
                      <td className="p-3 border-b text-right">{((comb.liters / totalLitros) * 100).toFixed(2)}%</td>
                      <td className="p-3 border-b text-right">{formatCurrency(comb.value)}</td>
                      <td className="p-3 border-b text-right">{((comb.value / totalGeral) * 100).toFixed(2)}%</td>
                      <td className="p-3 border-b text-right">{formatCurrency(comb.value / (comb.liters || 1))}</td>
                    </tr>
                  ))}
                  <tr className="bg-[#FF6600] text-white font-bold">
                    <td className="p-3">TOTAL</td>
                    <td className="p-3 text-right">{formatNumber(totalLitros)} L</td>
                    <td className="p-3 text-right">100%</td>
                    <td className="p-3 text-right">{formatCurrency(totalGeral)}</td>
                    <td className="p-3 text-right">100%</td>
                    <td className="p-3 text-right">{formatCurrency(totalGeral / (totalLitros || 1))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderComparativoReport = () => {
    if (loadingComparativo && !comparativoData) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-purple-600" size={48} />
        </div>
      );
    }

    const fmtCur = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    const fmtNum = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    const DeltaTag = ({ value, suffix = '%' }) => {
      if (value === 0) return <span className="text-slate-400 text-sm font-medium flex items-center gap-1"><Minus size={14} /> 0{suffix}</span>;
      const isUp = value > 0;
      return (
        <span className={`text-sm font-bold flex items-center gap-1 ${isUp ? 'text-red-500' : 'text-emerald-500'}`}>
          {isUp ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
          {isUp ? '+' : ''}{value.toFixed(1)}{suffix}
        </span>
      );
    };

    const DateBtn = ({ date, setDate, open, setOpen, label }) => (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 px-3 text-xs font-medium justify-start">
            <Calendar className="mr-1.5 h-3.5 w-3.5 text-purple-500" />
            {date ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarPicker mode="single" selected={date} onSelect={(d) => { setDate(d); setOpen(false); }} locale={ptBR} initialFocus />
        </PopoverContent>
      </Popover>
    );

    // Quick presets
    const applyPreset = (type) => {
      const n = new Date();
      if (type === 'semana') {
        const dayOfWeek = n.getDay();
        const thisMonStart = new Date(n); thisMonStart.setDate(n.getDate() - dayOfWeek + 1);
        const thisSunEnd = new Date(thisMonStart); thisSunEnd.setDate(thisMonStart.getDate() + 6);
        const lastMonStart = new Date(thisMonStart); lastMonStart.setDate(thisMonStart.getDate() - 7);
        const lastSunEnd = new Date(thisMonStart); lastSunEnd.setDate(thisMonStart.getDate() - 1);
        setPeriodAStart(thisMonStart); setPeriodAEnd(thisSunEnd);
        setPeriodBStart(lastMonStart); setPeriodBEnd(lastSunEnd);
      } else if (type === 'mes') {
        setPeriodAStart(new Date(n.getFullYear(), n.getMonth(), 1));
        setPeriodAEnd(new Date(n.getFullYear(), n.getMonth() + 1, 0));
        setPeriodBStart(new Date(n.getFullYear(), n.getMonth() - 1, 1));
        setPeriodBEnd(new Date(n.getFullYear(), n.getMonth(), 0));
      } else if (type === 'trimestre') {
        const q = Math.floor(n.getMonth() / 3);
        setPeriodAStart(new Date(n.getFullYear(), q * 3, 1));
        setPeriodAEnd(new Date(n.getFullYear(), q * 3 + 3, 0));
        setPeriodBStart(new Date(n.getFullYear(), (q - 1) * 3, 1));
        setPeriodBEnd(new Date(n.getFullYear(), (q - 1) * 3 + 3, 0));
      } else if (type === 'ano') {
        setPeriodAStart(new Date(n.getFullYear(), 0, 1));
        setPeriodAEnd(new Date(n.getFullYear(), 11, 31));
        setPeriodBStart(new Date(n.getFullYear() - 1, 0, 1));
        setPeriodBEnd(new Date(n.getFullYear() - 1, 11, 31));
      }
    };

    // Download PDF comparativo
    const downloadComparativoPdf = async () => {
      try {
        setDownloadingPdf('comparativo');
        const params = new URLSearchParams({
          periodo_a_inicio: format(periodAStart, 'yyyy-MM-dd'),
          periodo_a_fim: format(periodAEnd, 'yyyy-MM-dd'),
          periodo_b_inicio: format(periodBStart, 'yyyy-MM-dd'),
          periodo_b_fim: format(periodBEnd, 'yyyy-MM-dd'),
        });
        if (postosSelecionados.length > 0) params.set('postos', postosSelecionados.join(','));
        const response = await api.get(`/reports/comparativo-pdf?${params.toString()}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `comparativo_${format(periodAStart, 'yyyyMMdd')}_vs_${format(periodBStart, 'yyyyMMdd')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('PDF gerado!');
      } catch (error) {
        toast.error('Erro ao gerar PDF');
      } finally {
        setDownloadingPdf(null);
      }
    };

    const COMP_COLORS = { a: '#7C3AED', b: '#3B82F6' };
    const r = compResult;

    return (
      <div className="space-y-6">
        {/* Period Selection - Dark PowerBI style */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0 shadow-xl" data-testid="comparativo-header">
          <CardContent className="p-5">
            <div className="flex flex-col lg:flex-row lg:items-end gap-5">
              <div className="flex-1 space-y-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <ArrowLeftRight size={20} className="text-purple-400" /> Análise Comparativa
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[{l: 'Semana', k: 'semana'}, {l: 'Mês', k: 'mes'}, {l: 'Trimestre', k: 'trimestre'}, {l: 'Ano', k: 'ano'}].map(p => (
                    <Button key={p.k} variant="outline" size="sm" onClick={() => applyPreset(p.k)}
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-xs h-7" data-testid={`preset-${p.k}`}>
                      {p.l}
                    </Button>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-purple-300 uppercase tracking-wider">Período A (Atual)</p>
                    <div className="flex items-center gap-2">
                      <DateBtn date={periodAStart} setDate={setPeriodAStart} open={openPA1} setOpen={setOpenPA1} label="Início" />
                      <span className="text-slate-500 text-xs">até</span>
                      <DateBtn date={periodAEnd} setDate={setPeriodAEnd} open={openPA2} setOpen={setOpenPA2} label="Fim" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Período B (Referência)</p>
                    <div className="flex items-center gap-2">
                      <DateBtn date={periodBStart} setDate={setPeriodBStart} open={openPB1} setOpen={setOpenPB1} label="Início" />
                      <span className="text-slate-500 text-xs">até</span>
                      <DateBtn date={periodBEnd} setDate={setPeriodBEnd} open={openPB2} setOpen={setOpenPB2} label="Fim" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={fetchCompare} disabled={loadingComp} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="compare-btn">
                  {loadingComp ? <Loader2 className="animate-spin mr-2" size={16} /> : <TrendingUp className="mr-2" size={16} />}
                  Comparar
                </Button>
                <Button onClick={downloadComparativoPdf} disabled={downloadingPdf === 'comparativo'} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="download-comparativo-pdf">
                  <Download size={16} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loadingComp ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="animate-spin text-purple-500" size={40} />
          </div>
        ) : r ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Valor Total', a: r.period_a.total_value, b: r.period_b.total_value, delta: r.delta.value_pct, fmt: fmtCur, icon: '💰' },
                { label: 'Litros', a: r.period_a.total_liters, b: r.period_b.total_liters, delta: r.delta.liters_pct, fmt: (v) => `${fmtNum(v)} L`, icon: '⛽' },
                { label: 'Registros', a: r.period_a.count, b: r.period_b.count, delta: r.delta.count_pct, fmt: (v) => v, icon: '📋' },
                { label: 'Média/Registro', a: r.period_a.avg_per_record, b: r.period_b.avg_per_record, delta: r.delta.avg_pct, fmt: fmtCur, icon: '📊' },
              ].map((kpi, i) => (
                <Card key={i} className="border-0 shadow-lg bg-white" data-testid={`kpi-${i}`}>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500 font-medium mb-2">{kpi.icon} {kpi.label}</p>
                    <p className="text-xl font-bold text-slate-800 mb-1">{kpi.fmt(kpi.a)}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">Ref: {kpi.fmt(kpi.b)}</span>
                      <DeltaTag value={kpi.delta} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts Row 1: Barras Comparativas por Posto + Combustivel Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Card className="lg:col-span-2 shadow-lg border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <BarChart3 size={16} className="text-purple-500" /> Comparativo por Posto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(() => {
                        const all = new Set([...r.period_a.by_posto.map(p => p.name), ...r.period_b.by_posto.map(p => p.name)]);
                        return Array.from(all).map(name => ({
                          name: name.length > 15 ? name.substring(0, 15) + '..' : name,
                          'Período A': r.period_a.by_posto.find(p => p.name === name)?.value || 0,
                          'Período B': r.period_b.by_posto.find(p => p.name === name)?.value || 0,
                        }));
                      })()} margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => fmtCur(v)} />
                        <Legend />
                        <Bar dataKey="Período A" fill={COMP_COLORS.a} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Período B" fill={COMP_COLORS.b} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Fuel size={16} className="text-blue-500" /> Combustíveis (Período A)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie data={r.period_a.by_combustivel} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={3} stroke="none">
                          {r.period_a.by_combustivel.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtCur(v)} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {r.period_a.by_combustivel.map((c, i) => (
                      <span key={c.name} className="text-xs flex items-center gap-1 text-slate-600">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}></span>
                        {c.name}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2: Top Secretarias Comparison */}
            <Card className="shadow-lg border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Building2 size={16} className="text-indigo-500" /> Top Secretarias - Comparativo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(() => {
                      const all = new Set([...r.period_a.by_secretaria.map(s => s.name), ...r.period_b.by_secretaria.map(s => s.name)]);
                      return Array.from(all).slice(0, 6).map(name => ({
                        name: name.length > 12 ? name.substring(0, 12) + '..' : name,
                        'Período A': r.period_a.by_secretaria.find(s => s.name === name)?.value || 0,
                        'Período B': r.period_b.by_secretaria.find(s => s.name === name)?.value || 0,
                      }));
                    })()} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                      <Tooltip formatter={(v) => fmtCur(v)} />
                      <Legend />
                      <Bar dataKey="Período A" fill={COMP_COLORS.a} radius={[0, 4, 4, 0]} />
                      <Bar dataKey="Período B" fill={COMP_COLORS.b} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Interpretation Box */}
            <Card className={`border-l-4 shadow-lg ${r.delta.value_pct > 0 ? 'border-l-red-500 bg-red-50/50' : r.delta.value_pct < 0 ? 'border-l-emerald-500 bg-emerald-50/50' : 'border-l-slate-400 bg-slate-50'}`}>
              <CardContent className="p-5">
                <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <TrendingUp size={18} className="text-purple-600" /> Interpretação
                </h4>
                <div className="space-y-2 text-sm text-slate-700">
                  <p>
                    {r.delta.value_pct > 0
                      ? `Os gastos AUMENTARAM ${r.delta.value_pct.toFixed(1)}% (${fmtCur(Math.abs(r.delta.value_abs))} a mais) em relação ao período de referência.`
                      : r.delta.value_pct < 0
                      ? `Os gastos REDUZIRAM ${Math.abs(r.delta.value_pct).toFixed(1)}% (${fmtCur(Math.abs(r.delta.value_abs))} a menos) em relação ao período de referência.`
                      : 'Os gastos se mantiveram estáveis entre os períodos.'}
                  </p>
                  <p>
                    {r.delta.liters_pct > 0
                      ? `O consumo em litros aumentou ${r.delta.liters_pct.toFixed(1)}% (${fmtNum(Math.abs(r.delta.liters_abs))} L a mais).`
                      : r.delta.liters_pct < 0
                      ? `O consumo em litros reduziu ${Math.abs(r.delta.liters_pct).toFixed(1)}% (${fmtNum(Math.abs(r.delta.liters_abs))} L a menos).`
                      : 'O consumo em litros se manteve estável.'}
                  </p>
                  {Math.abs(r.delta.value_pct) > 15 && (
                    <p className={`font-semibold ${r.delta.value_pct > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {r.delta.value_pct > 0 ? '⚠ ATENÇÃO: Variação significativa nos gastos. Recomenda-se análise detalhada.' : '✓ POSITIVO: Redução expressiva nos gastos. Parabéns pelo controle!'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Monthly Evolution (from base data) */}
            {comparativoData?.monthly_chart && comparativoData.monthly_chart.length > 0 && (
              <Card className="shadow-lg border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={16} className="text-cyan-500" /> Evolução Mensal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={comparativoData.monthly_chart} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="gradPurple" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => fmtCur(v)} />
                        <Area type="monotone" dataKey="value" stroke="#7C3AED" strokeWidth={2} fill="url(#gradPurple)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <ArrowLeftRight size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Selecione os períodos e clique em <strong>Comparar</strong></p>
          </div>
        )}
      </div>
    );
  };

  const renderActiveReport = () => {
    switch (activeTab) {
      case 'secretarias':
        return renderSecretariasReport();
      case 'postos':
        return renderPostosReport();
      case 'veiculos':
        return renderVeiculosReport();
      case 'combustiveis':
        return renderCombustiveisReport();
      case 'comparativo':
        return renderComparativoReport();
      default:
        return renderSecretariasReport();
    }
  };

  return (
    <div className="space-y-6" data-testid="relatorios-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#0066CC] rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Central de Relatórios
            </h2>
            <p className="text-blue-100 mt-1">Dashboard profissional com gráficos e exportação em PDF</p>
          </div>
          <div className="flex items-center gap-2 bg-white/10 rounded-lg p-2">
            <TrendingUp size={24} />
            <div>
              <p className="text-sm opacity-80">Total Geral</p>
              <p className="text-xl font-bold">{formatCurrency(dashboardData.totals.value)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-l-4 border-l-[#0066CC]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-500" />
              <span className="font-semibold text-gray-700">Período:</span>
            </div>
            
            {/* Atalhos de mês */}
            <div className="flex flex-wrap gap-2">
              {(() => {
                const now = new Date();
                const currentMonth = now.getMonth();
                const currentYear = now.getFullYear();
                const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                
                // Últimos 3 meses
                const presets = [];
                for (let i = 0; i < 3; i++) {
                  let m = currentMonth - i;
                  let y = currentYear;
                  if (m < 0) { m += 12; y -= 1; }
                  const firstDay = `${y}-${String(m + 1).padStart(2, '0')}-01`;
                  const lastDay = new Date(y, m + 1, 0).getDate();
                  const lastDayStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                  presets.push({ label: `${months[m]}/${y}`, start: firstDay, end: lastDayStr });
                }
                
                return presets.map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setStartDate(preset.start); setEndDate(preset.end); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      startDate === preset.start && endDate === preset.end
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {preset.label}
                  </button>
                ));
              })()}
            </div>
            
            {/* Seleção personalizada */}
            <div className="flex flex-col md:flex-row gap-3 flex-1">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-gray-500" />
                <label className="text-sm text-gray-600">De:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  data-testid="filter-start-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-gray-500" />
                <label className="text-sm text-gray-600">Até:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  data-testid="filter-end-date"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => { setStartDate(''); setEndDate(''); setPostosSelecionados([]); setSecretariasSelecionadas([]); setCombustiveisSelecionados([]); }}
                className="text-sm"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
          
          {/* Info do período selecionado */}
          {(startDate || endDate) && (
            <div className="mt-3 p-2 bg-blue-50 rounded-lg text-sm text-blue-700 flex items-center gap-2">
              <Calendar size={16} />
              <span>
                <strong>Período selecionado:</strong> {startDate ? new Date(startDate + 'T12:00:00').toLocaleDateString('pt-BR') : 'Início'} 
                {' até '} 
                {endDate ? new Date(endDate + 'T12:00:00').toLocaleDateString('pt-BR') : 'Hoje'}
              </span>
              <span className="ml-2 text-blue-500">(Os gráficos e PDFs refletem este período)</span>
            </div>
          )}

          {/* Filtros de Postos */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Fuel size={16} className="text-red-600" />
                Filtrar por Posto
              </h4>
              <button 
                onClick={toggleAllPostos}
                className="text-xs text-blue-600 hover:underline"
              >
                {postosSelecionados.length === allPostos.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {allPostos.map(posto => (
                <button
                  key={posto.id}
                  onClick={() => togglePosto(posto.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    postosSelecionados.includes(posto.id)
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {posto.name}
                </button>
              ))}
              {allPostos.length === 0 && <span className="text-sm text-gray-400">Carregando postos...</span>}
            </div>
          </div>

          {/* Filtros de Secretarias */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Building2 size={16} className="text-blue-600" />
                Filtrar por Secretaria
              </h4>
              <button 
                onClick={toggleAllSecretarias}
                className="text-xs text-blue-600 hover:underline"
              >
                {secretariasSelecionadas.length === allSecretarias.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {allSecretarias.map(sec => (
                <button
                  key={sec.id}
                  onClick={() => toggleSecretaria(sec.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    secretariasSelecionadas.includes(sec.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {sec.name}
                </button>
              ))}
              {allSecretarias.length === 0 && <span className="text-sm text-gray-400">Carregando secretarias...</span>}
            </div>
          </div>

          {/* Filtro de Combustíveis - Dropdown Pesquisável */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-4">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Fuel size={16} className="text-orange-500" />
                Combustível:
              </h4>
              <FuelFilter
                combustiveis={combustiveisDisponiveis}
                selecionados={combustiveisSelecionados}
                onChange={setCombustiveisSelecionados}
                placeholder="Selecionar combustível"
                multiple={true}
                className="w-64"
              />
            </div>
          </div>

          {/* Indicador de filtros ativos */}
          {(postosSelecionados.length > 0 || secretariasSelecionadas.length > 0 || combustiveisSelecionados.length > 0) && (
            <div className="border-t pt-4">
              <p className="text-sm text-gray-600">
                <Filter size={14} className="inline mr-1" />
                Filtros ativos: 
                {postosSelecionados.length > 0 && <span className="text-red-600 font-medium ml-2">{postosSelecionados.length} posto(s)</span>}
                {secretariasSelecionadas.length > 0 && <span className="text-blue-600 font-medium ml-2">{secretariasSelecionadas.length} secretaria(s)</span>}
                {combustiveisSelecionados.length > 0 && <span className="text-orange-600 font-medium ml-2">{combustiveisSelecionados.length} combustivel(is)</span>}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conferencia Automatica dos Registros */}
      <Card className="border-l-4 border-l-[#003366]" data-testid="preview-section">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2 text-[#003366]">
              <Eye size={22} /> Conferencia dos Registros
            </span>
            <div className="flex items-center gap-3 flex-wrap">
              {selectedIds.size > 0 && (
                <span className="text-sm font-semibold bg-blue-100 text-blue-800 px-3 py-1 rounded-full" data-testid="selected-count-badge">
                  {selectedIds.size} selecionado(s) | {formatCurrency(selectedTotal)}
                </span>
              )}
              {previewData && (
                <span className="text-sm font-normal text-gray-500">
                  {filteredPreviewRecords.length} registro(s) | Total: <strong className="text-[#003366] text-base">{formatCurrency(filteredPreviewTotal)}</strong>
                </span>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Busca e botoes */}
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="relative max-w-xs flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por CA..."
                value={searchCA}
                onChange={(e) => setSearchCA(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-ca-input"
              />
            </div>
            {filteredPreviewRecords.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                className="h-9 text-xs"
                data-testid="toggle-select-all-btn"
              >
                {selectedIds.size === filteredPreviewRecords.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </Button>
            )}
            {selectedIds.size > 0 && (
              <Button
                onClick={downloadSelectedPDF}
                disabled={downloadingPdf === 'selecionados'}
                className="h-9 bg-[#003366] hover:bg-[#002244] text-white text-xs"
                data-testid="download-selected-pdf-btn"
              >
                {downloadingPdf === 'selecionados' ? (
                  <><Loader2 className="animate-spin mr-1" size={14} /> Gerando...</>
                ) : (
                  <><Download size={14} className="mr-1" /> Gerar Relatorio com {selectedIds.size} Selecionado(s)</>
                )}
              </Button>
            )}
          </div>

          {loadingPreview ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-[#003366]" size={28} />
              <span className="ml-2 text-gray-500 text-sm">Atualizando registros...</span>
            </div>
          ) : previewData && filteredPreviewRecords.length > 0 ? (
            <div className="overflow-auto max-h-[400px] border rounded-lg" data-testid="preview-table">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[#003366] text-white">
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">CA</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">Data</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">Secretaria</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">Nome Funcionario</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">Veiculo/Maquina</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">Posto</th>
                    <th className="px-3 py-2 text-left whitespace-nowrap">Combustivel</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Litros</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPreviewRecords.map((rec, idx) => {
                    const isBloqueado = rec.status_contabil === 'vermelho';
                    const isSelected = selectedIds.has(rec.abastecimento_id);
                    const postoColor = rec.posto ? {
                      'POSTO JP&HG LOBO': '#10B981',
                      'POSTO CARAUARI': '#EF4444',
                      'REDE BONS AMIGOS': '#3B82F6',
                      'PONTÃO BARROTE': '#6B7280',
                    }[rec.posto] || null : null;
                    return (
                      <tr
                        key={idx}
                        onClick={() => toggleSelectRecord(rec.abastecimento_id)}
                        className={`cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-50 ring-1 ring-inset ring-blue-300'
                            : idx % 2 === 0 ? 'bg-white hover:bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'
                        } ${isBloqueado ? 'opacity-60' : ''}`}
                        data-testid={`preview-row-${rec.ca}`}
                      >
                        <td className="px-2 py-1.5 text-center">
                          {isSelected
                            ? <CheckSquare size={16} className="text-blue-600 inline-block" />
                            : <Square size={16} className="text-gray-300 inline-block" />
                          }
                        </td>
                        <td className="px-3 py-1.5 font-medium whitespace-nowrap">
                          <span className={isBloqueado ? 'text-gray-400' : 'text-[#003366]'}>{rec.ca || '-'}</span>
                          {isBloqueado && <Lock size={12} className="inline-block ml-1 text-red-400" />}
                        </td>
                        <td className={`px-3 py-1.5 whitespace-nowrap ${isBloqueado ? 'text-gray-400' : ''}`}>{rec.date ? new Date(rec.date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</td>
                        <td className={`px-3 py-1.5 max-w-[200px] truncate ${isBloqueado ? 'text-gray-400' : ''}`} title={rec.secretaria}>{rec.secretaria}</td>
                        <td className={`px-3 py-1.5 uppercase ${isBloqueado ? 'text-gray-400' : ''}`}>{rec.funcionario_nome || '-'}</td>
                        <td className={`px-3 py-1.5 uppercase ${isBloqueado ? 'text-gray-400' : ''}`}>{rec.veiculo_nome || '-'}</td>
                        <td className="px-3 py-1.5 uppercase text-xs font-semibold" style={postoColor ? { color: postoColor } : {}}>{rec.posto || '-'}</td>
                        <td className={`px-3 py-1.5 uppercase ${isBloqueado ? 'text-gray-400' : ''}`}>{rec.descricao}</td>
                        <td className={`px-3 py-1.5 text-right ${isBloqueado ? 'text-gray-400' : ''}`}>{formatNumber(rec.litros)} L</td>
                        <td className={`px-3 py-1.5 text-right font-semibold ${isBloqueado ? 'text-gray-400 line-through' : 'text-green-700'}`}>{formatCurrency(rec.valor)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0">
                  <tr className="bg-[#003366] text-white font-bold">
                    <td className="px-2 py-2"></td>
                    <td className="px-3 py-2" colSpan={7}>TOTAL {selectedIds.size > 0 ? `(${selectedIds.size} selecionados)` : ''}</td>
                    <td className="px-3 py-2 text-right">{formatNumber(filteredPreviewRecords.filter(r => r.status_contabil !== 'vermelho').reduce((s, r) => s + r.litros, 0))} L</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(filteredPreviewTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : previewData ? (
            <p className="text-center text-gray-400 text-sm py-6">{searchCA ? 'Nenhum registro encontrado para esta CA' : 'Nenhum registro encontrado no periodo'}</p>
          ) : (
            <p className="text-center text-gray-400 text-sm py-6">Selecione um periodo para visualizar os registros</p>
          )}
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? `${tab.color} text-white shadow-lg`
                  : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <Icon size={20} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Download PDF Buttons */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {postosSelecionados.length > 0 && (
          <span className="text-xs bg-red-100 text-red-700 font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid="postos-filter-badge">
            <Fuel size={14} />
            Filtro: {postosSelecionados.length} posto{postosSelecionados.length > 1 ? 's' : ''}
          </span>
        )}
        <Button
          onClick={() => downloadPDF('geral')}
          disabled={downloadingPdf !== null}
          className="bg-gradient-to-r from-[#003366] to-[#0066CC] hover:from-[#002244] hover:to-[#004488] text-white px-6 py-3 rounded-lg flex items-center gap-2 shadow-lg"
          data-testid="download-pdf-geral"
        >
          {downloadingPdf === 'geral' ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Gerando Relatório Geral...
            </>
          ) : (
            <>
              <BarChart3 size={20} />
              Relatório Geral (PowerBI)
            </>
          )}
        </Button>
        <Button
          onClick={() => downloadPDF(activeTab)}
          disabled={downloadingPdf !== null}
          variant="outline"
          className="border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-6 py-3 rounded-lg flex items-center gap-2"
          data-testid="download-pdf-button"
        >
          {downloadingPdf === activeTab ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Gerando PDF...
            </>
          ) : (
            <>
              <Download size={20} />
              PDF da Aba Atual
            </>
          )}
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="animate-spin text-[#003366]" size={48} />
            <p className="text-gray-600">Carregando dados...</p>
          </div>
        </div>
      ) : (
        renderActiveReport()
      )}

      {/* Export All Section */}
      <Card className="bg-gradient-to-r from-gray-50 to-white">
        <CardHeader>
          <CardTitle className="text-[#003366]">Exportações Adicionais</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={() => api.get('/reports/excel', { responseType: 'blob' }).then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_completo.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Excel exportado!');
              }).catch(() => toast.error('Erro ao exportar'))}
              variant="outline"
              className="flex items-center gap-2 justify-center py-6"
              data-testid="export-excel-all"
            >
              <FileText size={24} className="text-green-600" />
              <div className="text-left">
                <p className="font-semibold">Exportar Excel Completo</p>
                <p className="text-sm text-gray-500">Todos os abastecimentos em planilha</p>
              </div>
            </Button>
            <Button
              onClick={() => downloadPDF('geral')}
              disabled={downloadingPdf !== null}
              variant="outline"
              className="flex items-center gap-2 justify-center py-6"
              data-testid="export-pdf-geral"
            >
              <BarChart3 size={24} className="text-blue-600" />
              <div className="text-left">
                <p className="font-semibold">Relatório Geral PDF (PowerBI)</p>
                <p className="text-sm text-gray-500">Relatório completo com gráficos visuais</p>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
